import type { AuthLogin, AuthPassword } from "~/types/schemas/autenticacao";
import { AutenticacaoApi } from "~/api/autenticacao/autenticacao";
import type { Usuario } from "~/types/schemas/usuario";
import { Warn } from "@sieg-ad/error";
import { USER_ROLES } from "~/constants/roles";
import { defineStore } from "pinia";


export type AuthState = {
  authenticated: boolean;
  user: Usuario | null;
  token: string | null;
  lastUpdate: Date;
  loading: boolean;
  message: string | null;
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    authenticated: false,
    user: null,
    token: null,
    lastUpdate: new Date(),
    loading: false,
    message: null,
  }),
  getters: {
    roleId: (state) => state.user?.tipo_id ?? null,
    isAdministrador: (state) => state.user?.tipo_id === USER_ROLES.ADMINISTRADOR,
    isGestor: (state) => state.user?.tipo_id === USER_ROLES.GESTOR,
    isDocente: (state) => state.user?.tipo_id === USER_ROLES.DOCENTE,
    isAluno: (state) => state.user?.tipo_id === USER_ROLES.ALUNO,
  },
  persist: {
    storage: piniaPluginPersistedstate.cookies({
      maxAge: 60 * 60 * 24 * 30,
      priority: "high",
    }),
    pick: ['authenticated', 'user', 'token'],
  },
  actions: {
    async authenticate(options: AuthLogin) {
      if (this.loading) return;

      this.loading = true;
      
      try {
        if (!options.login) throw new Warn('Preencha o campo de login');
        if (!options.senha) throw new Warn('Preencha o campo de senha');

        const { data } = await AutenticacaoApi.login(options);

        //@ts-ignore
        this.token = data.token;
        //@ts-ignore
        this.user = data.usuario;
        this.authenticated = true;
        this.lastUpdate = new Date();
      }
      catch (error) {
        this.message = error.message;
        throw error;
      }
      finally {
        this.loading = false;
      }
    },

    setUser(user: Usuario | null) {
      const current = JSON.stringify(this.user);  
      const newUser = JSON.stringify(user);

      if (current === newUser) return;
      this.user = user;
    },

    async changePassword(options: AuthPassword) {
      try {
        await AutenticacaoApi.senha(options);
        return { success: true }
      }
      catch (error) {
        return { success: false }
      }
    },

    async forgetPassword(options: Partial<AuthLogin>) {
      try {
        await AutenticacaoApi.forgetPassword(options);
        return { success: true }
      }
      catch (error) {
        return { success: false }
      }
    },

    async logout() {
      this.resetStore();
      useRouter().push('/login');
    },

    resetStore() {
      localStorage.clear();
      sessionStorage.clear();
      this.$reset();
    }
  }
})
